<div class="order-payment-modal">
    <div class="modal-header">
        <h4 class="modal-title" translate="cart.order.history.manage.payment"></h4>
        <button type="button" class="u-btn u-style-close close fa-solid fa-xmark"
                [attr.aria-label]="'cart.button.close' | translate"
                [disabled]="!canClose" (click)="close()"></button>
    </div>

    <div class="modal-body">
        <!-- No products / images. Total and credit rows appear only when something has already settled. -->
        <div class="order-payment-summary">
            <div class="summary-row">
                <span class="summary-label" translate="cart.order.history.order.number"></span>
                <span class="summary-value">{{ orderView.id }}</span>
            </div>
            <div class="summary-row">
                <span class="summary-label" translate="cart.order.history.order.status"></span>
                <span class="summary-value text-capitalize">{{ 'cart.order.history.order.status.' + orderView.status.toLowerCase() | translate }}</span>
            </div>
            @if (hasSettledAmount) {
                <div class="summary-row">
                    <span class="summary-label" translate="cart.order.history.payment.order.total"></span>
                    <span class="summary-value">{{ quote.orderTotal | usanacurrency: orderView.currencyCode | async }}</span>
                </div>
                <div class="summary-row summary-row-credit">
                    <span class="summary-label">
                        <span translate="cart.order.history.payment.credit.applied"></span>
                        <i class="fa-solid fa-circle-info summary-info" tabindex="0"
                           [tooltip]="'cart.order.history.payment.applied.subtext' | translate"
                           [attr.aria-label]="'cart.order.history.payment.applied.subtext' | translate"></i>
                    </span>
                    <span class="summary-value">-{{ quote.settledAmount | usanacurrency: orderView.currencyCode | async }}</span>
                </div>
            }
            @if (quote) {
                <div class="summary-row summary-row-total">
                    <span class="summary-label" translate="cart.order.history.payment.amount.due"></span>
                    <span class="summary-value">{{ quote.remainingBalance | usanacurrency: orderView.currencyCode | async }}</span>
                </div>
            }
        </div>

        <!-- Named up front so the background cancel is not a surprise. Hidden when the payment cannot be
             released, since nothing is going to replace it. -->
        @if (paymentsToReplace.length && state !== states.UNCANCELABLE && state !== states.ALREADY_PAID) {
            <div class="order-payment-warning">
                <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
                <div class="order-payment-warning-content">
                    <h4 class="order-payment-warning-title" translate="cart.order.history.payment.replacing.title"></h4>
                    <div class="order-payment-list">
                        @for (payment of paymentsToReplace; track payment.paymentId) {
                            <div class="order-payment-row">
                                <span>{{ payment.paymentMethodTypeDisplay || payment.paymentMethodType }}</span>
                                <span>{{ payment.amount | usanacurrency: orderView.currencyCode | async }}</span>
                            </div>
                        }
                    </div>
                </div>
            </div>
        }

        @switch (state) {
            @case (states.LOADING) {
                <div class="order-payment-loading">
                    <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                </div>
            }
            @case (states.ALREADY_PAID) {
                <div class="order-payment-already-paid">
                    <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
                    <span translate="cart.order.history.payment.already.paid"></span>
                </div>
            }
            @case (states.UNCANCELABLE) {
                <!-- The existing pending payment can't be released here, so point the customer at the only route
                     that can resolve it rather than offering a payment flow that would be refused. -->
                <div class="order-payment-uncancelable">
                    <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
                    <customer-service preText="cart.order.history.payment.not.cancelable"></customer-service>
                </div>
            }
            @default {
                <h4 class="section-heading" translate="cart.order.history.payment.method"></h4>
                <p class="payment-secure-subtext" translate="cart.checkout.express.payment.secure"></p>

                <div class="dsm-form">
                    <!-- Saved (on-file) methods: the connected card list express checkout uses. -->
                    @for (method of selectableMethods; track method.id) {
                        <div class="form-radio alinks" [ngClass]="{'active': method.id === selectedMethodId}"
                             (click)="selectMethod(method)">
                            <label class="form-radio-label">
                                <input type="radio" class="form-radio-input" name="orderPaymentMethod"
                                       [(ngModel)]="selectedMethodId" [value]="method.id"
                                       [disabled]="isSubmitting"/>
                                <card-content [summaryData]="method.displaySummary"></card-content>
                            </label>
                        </div>

                        <!-- Lite template is CVV-only: it re-associates the CVV with the stored card token,
                             which requireCVV2COF countries need before the card can be charged. -->
                        @if (method.id === cvvMethodId && !isSubmitting) {
                            <div class="order-payment-card-form">
                                <div class="dsm-form flex-form inner-panel-edit">
                                    <checkout-credit-card [selectedPaymentMethod]="method"
                                                          [openTemplate]="liteTemplate"
                                                          [allowSaveToFile]="false"
                                                          [modalLayout]="true"
                                                          parentCardName="orderPaymentCvv"
                                                          (nextEvent)="onCardEntered()"
                                                          (cancelEvent)="onCardCancel()"
                                                          (busyEvent)="onCardBusy($event)"></checkout-credit-card>
                                </div>
                            </div>
                        }
                    }

                    <!-- New card renders as an "+ Add" link until clicked, matching express checkout. -->
                    @if (cnofMethod && !addingNewCard) {
                        <div class="create-payment-container">
                            <a href="javascript:void(0)" [attr.data-automation]="cnofMethod.type"
                               (click)="startAddCard()">
                                <i class="fa-solid fa-plus" aria-hidden="true"></i>
                                <span translate="cart.order.history.payment.add.card"></span>
                            </a>
                        </div>
                    }

                    @if (!selectableMethods.length && !cnofMethod) {
                        <span translate="cart.order.history.payment.no.methods"></span>
                    }

                    <!-- modalLayout is the same mode express checkout's own payment modal uses: in-field
                         labels, committing the card on Save with its own Save / Cancel buttons. -->
                    @if (addingNewCard && !isSubmitting) {
                        <div class="order-payment-card-form">
                            <!-- Same wrapper checkout-payment-edit gives the card form in express: .dsm-form
                                 .flex-form .inner-panel-edit is what supplies the field and label widths. -->
                            <div class="dsm-form flex-form inner-panel-edit">
                                <checkout-credit-card [selectedPaymentMethod]="cnofMethod"
                                                      [allowSaveToFile]="false"
                                                      [modalLayout]="true"
                                                      [defaultBillingAddress]="orderView.address"
                                                      parentCardName="orderPaymentCard"
                                                      (nextEvent)="onCardEntered()"
                                                      (cancelEvent)="onCardCancel()"
                                                      (busyEvent)="onCardBusy($event)"></checkout-credit-card>
                            </div>
                        </div>
                    }
                </div>

                @if (errorMessage) {
                    <div class="error-text ban-icon-left margin-top-16">
                        <i class="fa-solid fa-ban" aria-hidden="true"></i>
                        <span translate="{{ errorMessage }}"></span>
                    </div>
                }

                @if (isSubmitting) {
                    <div class="order-payment-processing margin-top-16">
                        <i class="fa-solid fa-spinner fa-spin margin-right-8" aria-hidden="true"></i>
                        <span translate="cart.order.history.payment.processing"></span>
                    </div>
                }
            }
        }
    </div>

    <!-- Nothing to submit in either terminal state, so the footer offers only a way out. -->
    @if (state === states.UNCANCELABLE || state === states.ALREADY_PAID) {
        <div class="modal-footer">
            <button type="button" class="u-btn u-style-secondary u-shape-round u-size-small" (click)="close()">
                <span translate="cart.button.close"></span>
            </button>
        </div>
    }

    <!-- the card entry form brings its own Save / Cancel, so the modal footer stands down while it's open -->
    @if (state !== states.LOADING && state !== states.UNCANCELABLE && state !== states.ALREADY_PAID
            && !addingNewCard && !cvvMethodId) {
        <div class="modal-footer">
            <button type="button" class="u-btn u-style-tertiary u-shape-round u-size-small" (click)="close()" [disabled]="isSubmitting">
                <span translate="cart.checkout.cancel"></span>
            </button>
            <button type="button" class="u-btn u-style-primary-neutral u-shape-round u-size-small" (click)="submit()" [disabled]="!canSubmit">
                @if (isSubmitting) {
                    <i class="fa-solid fa-spinner fa-spin margin-right-8" aria-hidden="true"></i>
                }
                <span translate="cart.order.history.payment.submit"></span>
            </button>
        </div>
    }
</div>
